import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, Building2, Globe, MapPin, User, Users, Video } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type UserRef } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatMinutes, formatNumber } from '../lib/format';
import {
  MAX_MEETING_AGENDA,
  MAX_MEETING_LOCATION,
  MAX_MEETING_NOTE,
  MAX_MEETING_PEOPLE,
  atTime,
  dayKey,
  timeKey,
  type BusyPerson,
  type MeetingAudience,
  type MeetingDetail,
  type MeetingOptions,
  type MeetingPlace,
} from '../lib/meetings';
import { FormGrid, FormPage } from '../components/FormPage';
import { NameField, nameFits } from '../components/NameField';
import { PeoplePicker } from '../components/PeoplePicker';
import { Checkbox, Detail, Field, Input, Textarea, useToast } from '../components/ui';
import { meetingDay, meetingRefusal, meetingTimes } from './Meetings';

const AUDIENCES: Array<{ kind: MeetingAudience; icon: typeof User }> = [
  { kind: 'ONE_TO_ONE', icon: User },
  { kind: 'PEOPLE', icon: Users },
  { kind: 'DEPARTMENTS', icon: Building2 },
  { kind: 'EVERYONE', icon: Globe },
];

const PLACES: Array<{ kind: MeetingPlace; icon: typeof Building2 }> = [
  { kind: 'OFFICE', icon: Building2 },
  { kind: 'OUTSIDE', icon: MapPin },
  { kind: 'ONLINE', icon: Video },
];

/** Quick lengths beside the end time, in minutes. */
const LENGTHS = [15, 30, 45, 60, 90, 120];
const LINK = /^https?:\/\/[^\s/$.?#][^\s]*$/i;

interface Draft {
  title: string;
  audience: MeetingAudience;
  userIds: string[];
  departmentIds: string[];
  /** "2026-10-22" and "10:30" — the browser's own clock, as the inputs keep them. */
  date: string;
  start: string;
  end: string;
  place: MeetingPlace;
  location: string;
  agenda: string;
}

/** The next half hour from now, or 10:00 on another day. */
function suggestedStart(date: string): string {
  const now = new Date();
  if (date !== dayKey(now)) return '10:00';
  const next = new Date(now.getTime() + 30 * 60_000);
  next.setMinutes(next.getMinutes() < 30 ? 30 : 60, 0, 0);
  return next.getDate() === now.getDate() ? timeKey(next) : '23:30';
}

const plusMinutes = (time: string, minutes: number): string => {
  const at = atTime('2026-01-01', time);
  if (!at) return time;
  const next = new Date(at.getTime() + minutes * 60_000);
  // Same day only: a meeting that would run past midnight stops at 23:59.
  return next.getDate() === at.getDate() ? timeKey(next) : '23:59';
};

/**
 * Routes: `/meetings/new` and `/meetings/:id/edit`. What it is about; who it is
 * for — one other person, people picked, whole departments, or (for whoever sees
 * everyone) everyone; when; where — at the office, outside it or online; and its
 * agenda. Everyone asked is told and accepts or declines; moving it or changing
 * where it is asks them again. `?date=` and `?time=` fill in when, from the
 * calendar. Who is busy then is said as the form is filled in.
 */
export function MeetingFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  const existing = useQuery({
    queryKey: ['meetings', 'one', id],
    queryFn: () => api.get<MeetingDetail>(`/meetings/${id}`),
    enabled: editing,
  });
  const options = useQuery({
    queryKey: ['meetings', 'options'],
    queryFn: () => api.get<MeetingOptions>('/meetings/options'),
  });

  const [form, setForm] = useState<Draft>(() => {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(params.get('date') ?? '') ? params.get('date')! : dayKey(new Date());
    const start = /^\d{2}:\d{2}$/.test(params.get('time') ?? '') ? params.get('time')! : suggestedStart(date);
    return {
      title: '',
      audience: 'ONE_TO_ONE',
      userIds: [],
      departmentIds: [],
      date,
      start,
      end: plusMinutes(start, 30),
      place: 'OFFICE',
      location: '',
      agenda: '',
    };
  });
  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    setLoaded(true);
    const m = existing.data;
    const named = m.audience === 'ONE_TO_ONE' || m.audience === 'PEOPLE';
    setForm({
      title: m.title,
      audience: m.audience,
      userIds: named ? m.participants.map((p) => p.user.id) : [],
      departmentIds: m.departments.map((d) => d.id),
      date: dayKey(new Date(m.startsAt)),
      start: timeKey(new Date(m.startsAt)),
      end: timeKey(new Date(m.endsAt)),
      place: m.place,
      location: m.location ?? '',
      agenda: m.agenda ?? '',
    });
  }

  const o = options.data;
  const anyone = o?.anyone ?? false;
  // A meeting already for everyone keeps the choice when whoever called it changes it.
  const offered = AUDIENCES.filter((a) => a.kind !== 'EVERYONE' || anyone || existing.data?.audience === 'EVERYONE');
  // Names for whoever it already asks: they may stay, even from beyond the caller's departments.
  const known = new Map<string, UserRef>((existing.data?.participants ?? []).map((p) => [p.user.id, p.user]));
  const departments = [...(o?.departments ?? [])];
  for (const d of existing.data?.departments ?? []) {
    if (!departments.some((x) => x.id === d.id)) departments.push({ ...d, code: '', people: -1 });
  }

  // When, as instants.
  const startsAt = atTime(form.date, form.start);
  const endsAt = atTime(form.date, form.end);
  const minutes = startsAt && endsAt ? Math.round((endsAt.getTime() - startsAt.getTime()) / 60_000) : NaN;
  const before = existing.data;
  const startMoved = !before || !startsAt || startsAt.getTime() !== new Date(before.startsAt).getTime();
  const timeProblem: TranslationKey | null = !startsAt || !endsAt
    ? 'meetings.refusal.INVALID'
    : minutes <= 0
      ? 'meetings.refusal.ENDS_BEFORE_START'
      : minutes < 5
        ? 'meetings.refusal.TOO_SHORT'
        : startMoved && startsAt.getTime() < Date.now() - 60_000
          ? 'meetings.refusal.IN_THE_PAST'
          : null;

  const location = form.location.trim();
  const placeProblem: TranslationKey | null =
    form.place === 'OUTSIDE' && !location
      ? 'meetings.locationRequired'
      : form.place === 'ONLINE' && location && !LINK.test(location)
        ? 'meetings.linkInvalid'
        : null;

  const missingPeople = (form.audience === 'ONE_TO_ONE' || form.audience === 'PEOPLE') && form.userIds.length === 0;
  const missingDepartments = form.audience === 'DEPARTMENTS' && form.departmentIds.length === 0;
  const noReach = !editing && !!o && !anyone && o.departments.length === 0 && o.people.length === 0;

  // Moved, or somewhere else: everyone's answers start over.
  const asksAgain =
    !!before &&
    !!startsAt &&
    !!endsAt &&
    (startsAt.getTime() !== new Date(before.startsAt).getTime() ||
      endsAt.getTime() !== new Date(before.endsAt).getTime() ||
      form.place !== before.place ||
      (location || null) !== (before.location ?? null));

  // Who is busy then, of whoever it would ask — and you.
  const asking = !missingPeople && !missingDepartments && !timeProblem;
  const busy = useQuery({
    queryKey: ['meetings', 'busy', form.audience, form.userIds, form.departmentIds, startsAt?.toISOString(), endsAt?.toISOString(), id],
    queryFn: () =>
      api.post<{ people: BusyPerson[] }>('/meetings/busy', {
        startsAt: startsAt!.toISOString(),
        endsAt: endsAt!.toISOString(),
        audience: form.audience,
        ...(form.audience === 'ONE_TO_ONE' || form.audience === 'PEOPLE' ? { userIds: form.userIds } : {}),
        ...(form.audience === 'DEPARTMENTS' ? { departmentIds: form.departmentIds } : {}),
        ...(id ? { excludeId: id } : {}),
      }),
    enabled: asking && !!startsAt && !!endsAt,
    staleTime: 15_000,
  });

  // Started, done, cancelled or over — or not theirs: the API would refuse the save.
  const locked = editing && !!before && !before.can.edit;
  const ready =
    nameFits(form.title) && !missingPeople && !missingDepartments && !timeProblem && !placeProblem && !noReach && !!o && !locked;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: form.title.trim(),
        agenda: form.agenda.trim() || null,
        audience: form.audience,
        ...(form.audience === 'ONE_TO_ONE' || form.audience === 'PEOPLE' ? { userIds: form.userIds } : {}),
        ...(form.audience === 'DEPARTMENTS' ? { departmentIds: form.departmentIds } : {}),
        place: form.place,
        location: location || null,
        startsAt: startsAt!.toISOString(),
        endsAt: endsAt!.toISOString(),
      };
      return editing ? api.patch<MeetingDetail>(`/meetings/${id}`, body) : api.post<MeetingDetail>('/meetings', body);
    },
    onSuccess: (saved) => {
      toast.success(t(editing ? 'meetings.savedToast' : 'meetings.calledToast'));
      void queryClient.invalidateQueries({ queryKey: ['meetings'] });
      // In place of the form, so going back from the meeting does not reopen it.
      navigate(`/meetings/${saved.id}`, { replace: true });
    },
    onError: (e: Error) => toast.error(meetingRefusal(e, t)),
  });

  const setStart = (start: string) => {
    // The meeting keeps its length as its start moves.
    const length = Number.isFinite(minutes) && minutes > 0 ? minutes : 30;
    setForm({ ...form, start, end: plusMinutes(start, length) });
  };

  const setAudience = (audience: MeetingAudience) =>
    setForm({ ...form, audience, userIds: audience === 'ONE_TO_ONE' ? form.userIds.slice(0, 1) : form.userIds });

  const people = o?.people;
  const busyPeople = busy.data?.people ?? [];

  return (
    <FormPage
      title={editing ? t('meetings.editTitle') : t('meetings.newTitle')}
      subtitle={editing ? existing.data?.title : t('meetings.formSubtitle')}
      backTo={editing ? `/meetings/${id}` : undefined}
      backLabel={editing && existing.data ? existing.data.title : t('meetings.title')}
      submitLabel={editing ? t('common.save') : t('meetings.call')}
      submitting={save.isPending}
      disabled={!ready}
      loading={options.isLoading || (editing && existing.isLoading)}
      error={options.error ?? existing.error}
      onRetry={() => {
        void options.refetch();
        if (editing) void existing.refetch();
      }}
      onSubmit={() => save.mutate()}
      width="lg"
    >
      <div className="space-y-6">
        {locked && (
          <p className="rounded-md border border-warning/35 bg-warning/8 p-3 text-body-sm text-[var(--text-primary)]">
            {t(before?.status === 'STARTED' || before?.status === 'IN_PROGRESS' ? 'meetings.editLockedStarted' : 'meetings.notAllowed')}
          </p>
        )}
        <NameField
          label={t('meetings.titleLabel')}
          hint={t('meetings.titleHint')}
          value={form.title}
          dir="auto"
          onChange={(title) => setForm({ ...form, title })}
          autoFocus={!editing}
        />

        {/* Who it is for. */}
        <fieldset>
          <legend className="text-body-sm font-medium text-[var(--text-secondary)]">
            {t('meetings.whoFor')} <span className="text-primary dark:text-primary-400">*</span>
          </legend>
          {noReach && (
            <p className="mt-2 rounded-md border border-warning/35 bg-warning/8 p-3 text-body-sm text-[var(--text-primary)]">
              {t('meetings.noReach')}
            </p>
          )}
          <div
            className={clsx('mt-2 grid gap-2 sm:grid-cols-2', offered.length === 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3')}
            role="radiogroup"
          >
            {offered.map(({ kind, icon: Icon }) => (
              <label
                key={kind}
                className={clsx(
                  'flex cursor-pointer gap-2.5 rounded-md border p-3 transition-colors duration-fast',
                  form.audience === kind ? 'border-primary bg-primary/8' : 'hairline hover:border-primary/40',
                )}
              >
                <input
                  type="radio"
                  name="audience"
                  value={kind}
                  checked={form.audience === kind}
                  onChange={() => setAudience(kind)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-body-sm font-medium text-[var(--text-primary)]">
                    <Icon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                    {t(`meetings.audience.${kind}` as TranslationKey)}
                  </span>
                  <span className="mt-0.5 block text-caption text-muted">
                    {kind === 'EVERYONE'
                      ? t('meetings.audienceHint.EVERYONE', { n: o?.everyone != null ? formatNumber(o.everyone, locale) : '…' })
                      : t(`meetings.audienceHint${anyone || kind === 'ONE_TO_ONE' ? '' : 'Own'}.${kind}` as TranslationKey)}
                  </span>
                </span>
              </label>
            ))}
          </div>

          {(form.audience === 'ONE_TO_ONE' || form.audience === 'PEOPLE') && (
            <div className="mt-3">
              <PeoplePicker
                people={people}
                value={form.userIds}
                onChange={(userIds) => setForm({ ...form, userIds })}
                max={form.audience === 'ONE_TO_ONE' ? 1 : MAX_MEETING_PEOPLE}
                known={known}
                invalid={missingPeople}
                fullLabel={form.audience === 'ONE_TO_ONE' ? t('meetings.personFull') : undefined}
              />
              {missingPeople && (
                <p className="mt-1.5 text-caption text-warning-fg dark:text-warning-dark">
                  {t(form.audience === 'ONE_TO_ONE' ? 'meetings.pickPerson' : 'meetings.pickPeople')}
                </p>
              )}
            </div>
          )}

          {form.audience === 'DEPARTMENTS' && (
            <div className={clsx('mt-3 rounded-md border p-3', missingDepartments ? 'border-warning/35' : 'hairline')}>
              {departments.length === 0 ? (
                <p className="text-body-sm text-muted">{t('meetings.noDepartments')}</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {departments.map((d) => (
                    <Checkbox
                      key={d.id}
                      label={
                        <>
                          {localized(d)}{' '}
                          {d.people >= 0 && <span className="text-faint tabular">({formatNumber(d.people, locale)})</span>}
                        </>
                      }
                      checked={form.departmentIds.includes(d.id)}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          departmentIds: e.target.checked
                            ? [...form.departmentIds, d.id]
                            : form.departmentIds.filter((x) => x !== d.id),
                        })
                      }
                    />
                  ))}
                </div>
              )}
              {missingDepartments && departments.length > 0 && (
                <p className="mt-2 text-caption text-warning-fg dark:text-warning-dark">{t('meetings.pickDepartments')}</p>
              )}
            </div>
          )}

          {!anyone && <p className="mt-2 text-caption text-muted">{t('meetings.everyoneAdmins')}</p>}
        </fieldset>

        {/* When. */}
        <div>
          <FormGrid className="lg:grid-cols-3">
            <Field label={t('meetings.date')} required>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </Field>
            <Field label={t('meetings.starts')} required>
              <Input type="time" dir="ltr" className="tabular" value={form.start} onChange={(e) => setStart(e.target.value)} />
            </Field>
            <Field
              label={t('meetings.ends')}
              required
              error={timeProblem && (startsAt && endsAt) ? t(timeProblem) : null}
              hint={Number.isFinite(minutes) && minutes > 0 ? formatMinutes(minutes, locale) : undefined}
            >
              <Input type="time" dir="ltr" className="tabular" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
            </Field>
          </FormGrid>
          <div className="mt-2 flex flex-wrap items-center gap-1.5" role="group" aria-label={t('meetings.length')}>
            <span className="text-caption text-muted">{t('meetings.length')}:</span>
            {LENGTHS.map((length) => (
              <button
                key={length}
                type="button"
                onClick={() => setForm({ ...form, end: plusMinutes(form.start, length) })}
                aria-pressed={minutes === length}
                className={clsx(
                  'rounded-pill px-2.5 py-0.5 text-caption tabular ring-1 ring-inset transition-colors duration-fast',
                  minutes === length
                    ? 'bg-primary/12 text-primary-600 ring-primary/40 dark:text-primary-400'
                    : 'text-[var(--text-secondary)] ring-[var(--line)] hover:ring-primary/40',
                )}
              >
                {formatMinutes(length, locale)}
              </button>
            ))}
          </div>
          <p className="mt-2 text-caption text-faint">
            {o && o.reminderMinutes > 0
              ? t('meetings.reminderNote', { time: formatMinutes(o.reminderMinutes, locale) })
              : t('meetings.reminderNoteOff')}
          </p>

          {busyPeople.length > 0 && (
            <div className="mt-3 rounded-md border border-warning/35 bg-warning/8 p-3">
              <p className="flex items-center gap-1.5 text-body-sm font-medium text-[var(--text-primary)]">
                <AlertTriangle className="h-4 w-4 text-warning-fg dark:text-warning-dark" aria-hidden />
                {t('meetings.busyTitle')}
              </p>
              <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-body-sm">
                {busyPeople.map((p) => (
                  <li key={p.id} className="text-[var(--text-primary)]">
                    <bdi>{p.you ? t('meetings.busyYou') : localized(p)}</bdi>
                    <span className="text-muted">
                      {' — '}
                      {[p.meeting ? t('meetings.busyMeeting') : null, p.leave ? t('meetings.busyLeave') : null]
                        .filter(Boolean)
                        .join(locale === 'ar' ? '، ' : ', ')}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-caption text-muted">{t('meetings.busyHint')}</p>
            </div>
          )}
        </div>

        {/* Where. */}
        <fieldset>
          <legend className="text-body-sm font-medium text-[var(--text-secondary)]">
            {t('meetings.where')} <span className="text-primary dark:text-primary-400">*</span>
          </legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-3" role="radiogroup">
            {PLACES.map(({ kind, icon: Icon }) => (
              <label
                key={kind}
                className={clsx(
                  'flex cursor-pointer gap-2.5 rounded-md border p-3 transition-colors duration-fast',
                  form.place === kind ? 'border-primary bg-primary/8' : 'hairline hover:border-primary/40',
                )}
              >
                <input
                  type="radio"
                  name="place"
                  value={kind}
                  checked={form.place === kind}
                  onChange={() => setForm({ ...form, place: kind })}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-body-sm font-medium text-[var(--text-primary)]">
                    <Icon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                    {t(`meetings.place.${kind}` as TranslationKey)}
                  </span>
                  <span className="mt-0.5 block text-caption text-muted">{t(`meetings.placeHint.${kind}` as TranslationKey)}</span>
                </span>
              </label>
            ))}
          </div>
          <Field
            label={t(`meetings.locationLabel.${form.place}` as TranslationKey)}
            required={form.place === 'OUTSIDE'}
            error={placeProblem && (form.place === 'ONLINE' || form.location !== '') ? t(placeProblem) : null}
            hint={form.place === 'ONLINE' ? t('meetings.linkHint') : undefined}
            className="mt-3"
          >
            {form.place === 'OUTSIDE' ? (
              <Textarea
                rows={2}
                value={form.location}
                maxLength={MAX_MEETING_LOCATION}
                dir="auto"
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="min-h-[64px]"
              />
            ) : (
              <Input
                type={form.place === 'ONLINE' ? 'url' : 'text'}
                dir={form.place === 'ONLINE' ? 'ltr' : 'auto'}
                placeholder={form.place === 'ONLINE' ? 'https://' : undefined}
                value={form.location}
                maxLength={MAX_MEETING_LOCATION}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              />
            )}
          </Field>
        </fieldset>

        <Field label={t('meetings.agendaLabel')} hint={t('meetings.agendaHint')}>
          <Textarea
            rows={5}
            value={form.agenda}
            maxLength={MAX_MEETING_AGENDA}
            dir="auto"
            onChange={(e) => setForm({ ...form, agenda: e.target.value })}
          />
        </Field>

        {asksAgain && (
          <p className="rounded-md border border-warning/35 bg-warning/8 p-3 text-body-sm text-[var(--text-primary)]">
            {t('meetings.askAgainNote')}
          </p>
        )}
      </div>
    </FormPage>
  );
}

/**
 * Routes: `/meetings/:id/decline` and `/meetings/:id/cancel` — a word on why,
 * optional: whoever called it reads a decline, everyone asked reads a
 * cancellation. Back the way they came.
 */
export function MeetingNotePage({ action }: { action: 'decline' | 'cancel' }) {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const meeting = useQuery({
    queryKey: ['meetings', 'one', id],
    queryFn: () => api.get<MeetingDetail>(`/meetings/${id}`),
  });

  const send = useMutation({
    mutationFn: () => api.post(`/meetings/${id}/${action}`, { note: note.trim() || undefined }),
    onSuccess: () => {
      toast.success(t(action === 'decline' ? 'meetings.declinedToast' : 'meetings.cancelledToast'));
      void queryClient.invalidateQueries({ queryKey: ['meetings'] });
      if (location.key !== 'default') navigate(-1);
      else navigate(`/meetings/${id}`, { replace: true });
    },
    onError: (e: Error) => toast.error(meetingRefusal(e, t)),
  });

  const m = meeting.data;
  const allowed = m ? (action === 'decline' ? m.can.answer : m.can.cancel) : false;

  return (
    <FormPage
      title={t(action === 'decline' ? 'meetings.declineTitle' : 'meetings.cancelTitle')}
      backLabel={t('common.back')}
      submitLabel={t(action === 'decline' ? 'meetings.decline' : 'meetings.cancel')}
      destructive
      submitting={send.isPending}
      disabled={!!m && !allowed}
      loading={meeting.isLoading}
      error={meeting.error}
      onRetry={() => void meeting.refetch()}
      onSubmit={() => send.mutate()}
      summary={
        m && (
          <dl>
            <Detail label={t('meetings.titleLabel')}>
              <bdi>{m.title}</bdi>
            </Detail>
            <Detail label={t('meetings.date')}>
              <span className="tabular">
                {meetingDay(m.startsAt, locale)} · {meetingTimes(m, locale)}
              </span>
            </Detail>
            <Detail label={t('meetings.where')}>{t(`meetings.place.${m.place}` as TranslationKey)}</Detail>
            <Detail label={t('meetings.calledBy')}>{localized(m.organizer)}</Detail>
            {action === 'cancel' && (
              <Detail label={t('meetings.answers')}>
                {t('meetings.answersShort', { accepted: m.answers.accepted, total: m.answers.total })}
              </Detail>
            )}
          </dl>
        )
      }
    >
      {m && !allowed && (
        <p className="mb-3 text-body-sm text-warning-fg dark:text-warning-dark">
          {t(
            action === 'cancel' && (m.status === 'STARTED' || m.status === 'IN_PROGRESS')
              ? 'meetings.editLockedStarted'
              : 'meetings.notAllowed',
          )}
        </p>
      )}
      <Field
        label={t(action === 'decline' ? 'meetings.declineNote' : 'meetings.cancelNote')}
        hint={t(action === 'decline' ? 'meetings.declineNoteHint' : 'meetings.cancelNoteHint')}
      >
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={MAX_MEETING_NOTE} dir="auto" autoFocus />
      </Field>
    </FormPage>
  );
}
